import { useState } from 'react'
import type { AdSlotType, AdsCatalog, DeviceHeight, DeviceSizes, Size } from '@/lib/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChevronRight } from 'lucide-react'
import { Field, SizesInput } from '../components/fields'
import { Triple, TypeCode } from '../components/cells'

const DEVICES: (keyof DeviceSizes)[] = ['desktop', 'tablet', 'mobile']
// ponytail: tymczasowo ukryte w edytorze — typy zostają w katalogu i działają na froncie.
export const HIDDEN_TYPES = new Set(['HEADER', 'SCREEN'])

export function TypesEditor({
  catalog,
  onChangeCatalog,
}: {
  catalog: AdsCatalog
  onChangeCatalog: (next: AdsCatalog) => void
}) {
  const [newId, setNewId] = useState('')
  const [newName, setNewName] = useState('')
  const [openType, setOpenType] = useState<string | null>(catalog.types[0]?.id ?? null)

  function addType() {
    const id = newId.trim().toUpperCase()
    if (!id || catalog.types.some((t) => t.id === id)) return
    const nextType: AdSlotType = {
      id,
      name: newName.trim() || id,
      sizes: { desktop: [[300, 250]], tablet: [[300, 250]], mobile: [[300, 250]] },
      adHigh: { desktop: 250, tablet: 250, mobile: 250 },
    }
    onChangeCatalog({
      ...catalog,
      types: [...catalog.types, nextType],
    })
    setNewId('')
    setNewName('')
    setOpenType(id)
  }

  function updateType(idx: number, next: AdSlotType) {
    onChangeCatalog({
      ...catalog,
      types: catalog.types.map((t, i) => (i === idx ? next : t)),
    })
  }

  function removeType(id: string) {
    const usedBy = catalog.adUnits.filter((u) => u.typeId === id)
    if (usedBy.length > 0) {
      window.alert(
        `Nie można usunąć typu „${id}”, ponieważ korzysta z niego ${usedBy.length} adUnitów (${usedBy.map((u) => u.slot).join(', ')}). Najpierw zmień im typ lub je usuń.`,
      )
      return
    }
    onChangeCatalog({
      ...catalog,
      types: catalog.types.filter((t) => t.id !== id),
    })
  }

  function applyDefaultHighToUnits(type: AdSlotType) {
    onChangeCatalog({
      ...catalog,
      adUnits: catalog.adUnits.map((u) =>
        u.typeId === type.id ? { ...u, adHigh: { ...type.adHigh } } : u,
      ),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Wspólne Typy Reklam
            </span>
            <Badge variant="secondary">SW · RB · BB · AB</Badge>
          </div>
          <p className="max-w-2xl text-xs text-slate-500">
            Typy reklam definiują rozmiary (GAM + Prebid) oraz domyślną wysokość placeholdera dla
            wszystkich serwisów jednocześnie. Zmiana rozmiarów w typie automatycznie aktualizuje
            wszystkie przypisane do niego AdUnity we wszystkich aplikacjach.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <Field label="Kod typu (np. XL, L, HALF)">
            <input
              value={newId}
              onChange={(e) => setNewId(e.target.value.toUpperCase())}
              placeholder="np. XL_CUSTOM"
              className="w-36 rounded border border-slate-200 bg-white px-2 py-1 font-mono text-sm outline-none focus:border-slate-400"
            />
          </Field>
          <Field label="Nazwa wyświetlana">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="np. Billboard XL"
              className="w-44 rounded border border-slate-200 bg-white px-2 py-1 text-sm outline-none focus:border-slate-400"
            />
          </Field>
          <Button
            type="button"
            size="sm"
            onClick={addType}
            disabled={
              !newId.trim() || catalog.types.some((t) => t.id === newId.trim().toUpperCase())
            }
          >
            + Dodaj typ
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200">
        <Table>
          <TableHeader className="bg-slate-50 text-xs uppercase tracking-wide">
            <TableRow className="hover:bg-slate-50">
              <TableHead className="w-10" />
              <TableHead className="text-slate-500">Typ</TableHead>
              <TableHead className="text-slate-500">Nazwa</TableHead>
              <TableHead className="text-slate-500">Placeholder D · T · M</TableHead>
              <TableHead className="text-slate-500">AdUnity</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {catalog.types.map((type, idx) => {
              if (HIDDEN_TYPES.has(type.id)) return null
              const units = catalog.adUnits.filter((u) => u.typeId === type.id)
              const isOpen = openType === type.id
              return (
                <TypeRows
                  key={type.id}
                  type={type}
                  unitNames={units.map((u) => u.slot)}
                  open={isOpen}
                  onToggle={() => setOpenType(isOpen ? null : type.id)}
                  onChange={(next) => updateType(idx, next)}
                  onRemove={() => removeType(type.id)}
                  onApplyHighToUnits={() => applyDefaultHighToUnits(type)}
                />
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function TypeRows({
  type,
  unitNames,
  open,
  onToggle,
  onChange,
  onRemove,
  onApplyHighToUnits,
}: {
  type: AdSlotType
  unitNames: string[]
  open: boolean
  onToggle: () => void
  onChange: (next: AdSlotType) => void
  onRemove: () => void
  onApplyHighToUnits: () => void
}) {
  function setDeviceSizes(device: keyof DeviceSizes, sizes: Size[]) {
    onChange({ ...type, sizes: { ...type.sizes, [device]: sizes } })
  }

  function setDeviceHigh(device: keyof DeviceHeight, val: number) {
    onChange({ ...type, adHigh: { ...type.adHigh, [device]: val } })
  }

  return (
    <>
      <TableRow
        onClick={onToggle}
        aria-expanded={open}
        className={'cursor-pointer ' + (open ? 'border-b-0 bg-slate-50' : '')}
      >
        <TableCell className="text-slate-400">
          <ChevronRight className={'size-4 transition-transform ' + (open ? 'rotate-90' : '')} />
        </TableCell>
        <TableCell>
          <TypeCode id={type.id} />
        </TableCell>
        <TableCell className="font-medium text-slate-800">{type.name}</TableCell>
        <TableCell>
          <Triple
            values={[type.adHigh.desktop, type.adHigh.tablet, type.adHigh.mobile]}
            unit="px"
          />
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="font-mono">
            {unitNames.length}
          </Badge>
        </TableCell>
        <TableCell className="text-right">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          >
            Usuń
          </Button>
        </TableCell>
      </TableRow>

      {open && (
        <TableRow className="bg-slate-50 hover:bg-slate-50">
          <TableCell colSpan={6} className="whitespace-normal px-4 pb-4 pt-0">
            <div className="space-y-4 rounded-md border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <Field label="Nazwa typu">
                  <input
                    value={type.name}
                    onChange={(e) => onChange({ ...type, name: e.target.value })}
                    className="w-64 rounded border border-slate-200 bg-white px-2 py-1 text-sm outline-none focus:border-slate-400"
                  />
                </Field>
                {unitNames.length > 0 && (
                  <Button type="button" variant="outline" size="sm" onClick={onApplyHighToUnits}>
                    Ustaw ten adHigh na wszystkich {unitNames.length} adUnitach typu {type.id}
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {DEVICES.map((device) => (
                  <fieldset
                    key={device}
                    className="rounded border border-slate-200 bg-slate-50 p-3"
                  >
                    <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                      {device}
                    </legend>
                    <div className="mb-2">
                      <span className="mb-1 block text-[11px] uppercase tracking-wide text-slate-500">
                        Rozmiary (GAM + Prebid)
                      </span>
                      <SizesInput
                        value={type.sizes[device]}
                        onChange={(next) => setDeviceSizes(device, next)}
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <span className="text-[11px] uppercase tracking-wide text-slate-500">
                        Domyślny placeholder (adHigh px)
                      </span>
                      <input
                        type="number"
                        min={0}
                        value={type.adHigh[device]}
                        onChange={(e) => setDeviceHigh(device, Number(e.target.value) || 0)}
                        className="w-24 rounded border border-slate-200 bg-white px-2 py-0.5 font-mono text-sm outline-none focus:border-slate-400"
                      />
                    </label>
                  </fieldset>
                ))}
              </div>

              {unitNames.length > 0 && (
                <div className="rounded border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <span className="font-medium">AdUnity tego typu ({unitNames.length}): </span>
                  <span className="font-mono">{unitNames.join(', ')}</span>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}
